// Libraries
import * as yup from "yup";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";

// Images
import symbol_1 from "@assets/img/logo/logo-01.webp";

// Components
import { Button } from "@components/buttons/button";
import { api } from "@service/api";
import { InputGroup } from "@components/forms/input-group";
import { ZuAuthStore } from "@hooks/stores/zustand-auth-store";
import { CustomerPasswordChangedEnum } from "@hooks/dtos/user-dto";

interface IFormValues {
  oldPassword: string;
  password: string;
  passwordConfirm: string;
}

export function ChangePassword() {
  const { customer } = ZuAuthStore();
  const navigate = useNavigate();
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  const scheme = yup.object().shape({
    oldPassword: yup.string().required("A senha atual é obrigatória"),
    password: yup
      .string()
      .required("A nova senha é obrigatória")
      .min(6, "A nova senha deve ter pelo menos 6 caracteres")
      .notOneOf(
        [yup.ref("oldPassword")],
        "A nova senha não pode ser igual à atual"
      ),
    passwordConfirm: yup
      .string()
      .required("A confirmação da nova senha é obrigatória")
      .oneOf([yup.ref("password")], "As senhas não conferem"),
  });

  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormValues>({
    resolver: yupResolver(scheme) as any,
    mode: "onSubmit",
    reValidateMode: "onBlur",
  });

  async function handleChange(form: IFormValues) {
    try {
      const data = {
        oldPassword: form.oldPassword,
        password: form.password,
        passwordConfirm: form.passwordConfirm,
      };
      await api.post(`/customer//password/update`, data);

      pushToastify({
        message: "Seu senha foi alterada com sucesso.",
        type: "success",
        theme: "dark",
      });

      setTimeout(() => {
        navigate("/dashboard");
      }, 1500);
    } catch (error: any) {
      pushToastify({
        message: error?.response?.data.message ?? "Ocorreu um erro!",
        type: "error",
        theme: "dark",
      });
    }
  }

  function handleKeyPress(event: KeyboardEvent) {
    if (event.key === "Enter") {
      const activeElement = document.activeElement;
      if (
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement
      ) {
        activeElement.blur();
      }
      event.preventDefault();
      handleSubmit(handleChange)();
    }
  }

  useEffect(() => {
    document.addEventListener("keydown", handleKeyPress);
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, []);

  return (
    <>
      <div className="container-fluid d-flex justify-content-center pt-5">
        <div className="row justify-content-center w-100">
          <div className="col-12 col-md-8 col-lg-6 col-xl-5">
            {/* Image Above of the Form */}
            <div className="text-center">
              <img
                src={symbol_1}
                className="img-fluid rounded-4"
                style={{
                  maxHeight: "300px",
                  width: "100%",
                  maxWidth: "600px",
                  filter: "grayscale(100%)",
                }}
                alt="symbol_1"
              />
            </div>

            {/* Info Form */}
            <div className="d-flex flex-column justify-content-center align-items-center">
              <div className="col-12 d-flex flex-column px-3 px-md-4">
                <span className="text-center fs-3 fw-bold mt-3">
                  Alteração de Senha
                </span>
                <span className="text-center fs-6 fs-md-5 text-muted my-2 mb-md-2">
                  {customer &&
                  customer.customer.pswdChanged ===
                    CustomerPasswordChangedEnum.FALSE
                    ? `A alteração de senha é OBRIGATÓRIA no primeiro acesso.`
                    : `Bem-vindo à página de alteração de senha.`}
                </span>

                {/* Form */}
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(handleChange)();
                  }}
                >
                  <div className="pt-4">
                    <span className="ms-1 text-muted">
                      Preencha os campos abaixo para prosseguir:
                    </span>
                    {/* Old Password */}
                    <div className="col-md-12 mb-3">
                      <InputGroup
                        label=""
                        placeholder="Senha atual"
                        type="password"
                        register={register("oldPassword")}
                        errorMessage={errors?.oldPassword?.message}
                      />
                    </div>

                    {/* New Password */}
                    <div className="col-md-12 mb-3">
                      <InputGroup
                        label=""
                        placeholder="Nova Senha"
                        type="password"
                        register={register("password")}
                        errorMessage={errors?.password?.message}
                      />
                    </div>

                    {/* Confirm Password */}
                    <div className="col-md-12 mb-3">
                      <InputGroup
                        label=""
                        placeholder="Repita a Nova Senha"
                        type="password"
                        register={register("passwordConfirm")}
                        errorMessage={errors?.passwordConfirm?.message}
                      />
                    </div>

                    {/* Submit Button */}
                    <div className="col-md-12 my-3 col-12">
                      <Button
                        children="Salvar"
                        className="btn btn-secondary text-white w-100 py-3"
                      />
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
